import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

const CATEGORIES = [
  { value: 'technical_support', label: 'Technical Support' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'business_inquiry', label: 'Business Inquiry' },
  { value: 'general_question', label: 'General Question' },
  { value: 'bug_report', label: 'Bug Report' },
];

function LandingPage() {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email address';
    if (!formData.category) errs.category = 'Please select a category';
    if (!formData.subject.trim()) errs.subject = 'Subject is required';
    if (!formData.message.trim()) errs.message = 'Message is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitResult(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitResult({ type: 'success', message: data.message });
        setFormData({ name: '', email: '', category: '', subject: '', message: '' });
        setErrors({});
      } else {
        setSubmitResult({ type: 'error', message: data.error || 'Something went wrong' });
      }
    } catch {
      setSubmitResult({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-primary-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(14,165,233,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(139,92,246,0.2) 0%, transparent 50%)'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
              AI-Powered <span className="text-primary-400">Legal Research</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-gray-300">
              Research legal questions, review contracts, and study legislation with the power of artificial intelligence. Built on a comprehensive database of U.S. federal and state law.
            </p>
            <p className="mt-3 max-w-xl mx-auto text-sm text-gray-400">
              Legalia provides legal information, not legal advice. Not a substitute for a licensed attorney.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/qa"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-lg"
              >
                Try Legal Q&A
                <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <Link
                to="/contract-analysis"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border-2 border-gray-400 text-base font-medium rounded-lg text-white hover:bg-white hover:text-gray-900 transition-colors"
              >
                Analyze a Contract
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">What Legalia Can Do</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Research tools to help you explore the legal landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Legal Q&A */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-6">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal Q&A</h3>
              <p className="text-gray-600 mb-4">
                Research any legal question and get AI-generated information backed by real legal sources. Legalia searches a database of federal statutes, case law, regulations, and constitutional provisions to ground every response in actual law.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                No account required. Results include source citations, legal foundation diagrams, and historical timelines.
              </p>
              <Link to="/qa" className="text-primary-600 font-medium hover:text-primary-700 inline-flex items-center">
                Ask a question
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* Contract Analysis */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-6">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Contract Review</h3>
              <p className="text-gray-600 mb-4">
                Upload a contract (PDF or DOCX) and get an AI-generated review including clause identification, risk flagging, obligation mapping, and a visual contract flow diagram. Results are informational — consult an attorney for legal advice.
              </p>
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                Requires a free account. Your contracts are stored securely and kept private to your account.
              </p>
              <Link to="/contract-analysis" className="text-purple-600 font-medium hover:text-purple-700 inline-flex items-center">
                Analyze a contract
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* Law Analysis */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-6">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Law Analysis</h3>
              <p className="text-gray-600 mb-4">
                Paste a URL to any piece of legislation and Legalia will fetch, parse, and analyze it across multiple dimensions including constitutional alignment, enforcement mechanisms, economic impact, and more.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Results include scatter plots, radar charts, and detailed textual analysis for each dimension.
              </p>
              <Link to="/law-analysis" className="text-green-600 font-medium hover:text-green-700 inline-flex items-center">
                Analyze legislation
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* Legal Strategy — admin only */}
            {isAdmin && (
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-6">
                <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal Strategy</h3>
              <p className="text-gray-600 mb-4">
                Research legal pathways with multi-jurisdictional law comparisons, scenario modeling, and risk analysis. For internal research use only.
              </p>
              <p className="text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2 mb-4">
                Admin only — internal research tool.
              </p>
              <Link to="/legal-strategy" className="text-amber-600 font-medium hover:text-amber-700 inline-flex items-center">
                Open strategy tool
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* How Q&A Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">How Legal Q&A Works</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Every answer is grounded in real legal sources through retrieval-augmented generation.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold text-gray-900 mb-2">Ask Your Question</h3>
              <p className="text-sm text-gray-600">Type any legal question in plain English. Legalia handles the rest.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold text-gray-900 mb-2">Source Retrieval</h3>
              <p className="text-sm text-gray-600">Legalia searches its database of legal documents using vector embeddings to find the most relevant statutes, cases, and regulations.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold text-gray-900 mb-2">AI Analysis</h3>
              <p className="text-sm text-gray-600">The retrieved sources are fed to an AI model alongside your question to generate an answer grounded in actual law.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-4 text-xl font-bold">4</div>
              <h3 className="font-semibold text-gray-900 mb-2">Cited Results</h3>
              <p className="text-sm text-gray-600">You get a detailed answer with source citations, a legal foundation diagram, and a historical timeline of relevant law.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Get In Touch</h2>
            <p className="mt-4 text-lg text-gray-600">
              Have a question, feedback, or business inquiry? We'd love to hear from you.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8">
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={e => handleChange('name', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder="Your name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={e => handleChange('email', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder="you@example.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="contact-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  id="contact-category"
                  value={formData.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.category ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
              </div>

              <div className="mt-6">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  id="contact-subject"
                  type="text"
                  value={formData.subject}
                  onChange={e => handleChange('subject', e.target.value)}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.subject ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="What is this about?"
                />
                {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
              </div>

              <div className="mt-6">
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  id="contact-message"
                  rows={5}
                  value={formData.message}
                  onChange={e => handleChange('message', e.target.value)}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical ${errors.message ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Tell us more..."
                />
                {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
              </div>

              {submitResult && (
                <div className={`mt-6 p-4 rounded-lg text-sm ${
                  submitResult.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitResult.message}
                </div>
              )}

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <span className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Legalia" className="h-8 w-8 object-contain" />
              <span className="text-white font-semibold text-lg">Legalia</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/qa" className="hover:text-white transition-colors">Legal Q&A</Link>
              <Link to="/contract-analysis" className="hover:text-white transition-colors">Contract Analysis</Link>
              <Link to="/law-analysis" className="hover:text-white transition-colors">Law Analysis</Link>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm">&copy; {new Date().getFullYear()} Legalia. All rights reserved.</p>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-xs text-gray-500 space-y-2">
            <p className="font-semibold text-gray-400">Legal Information Disclaimer</p>
            <p>
              Legalia is an AI-powered legal research and information tool. It does not provide legal advice,
              and no attorney-client relationship is created by using this service. Legalia is not a law firm and does not
              provide legal representation. AI-generated analysis may contain errors and should be independently verified.
              Always consult a licensed attorney before making legal decisions.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
